import { zodResolver } from "@hookform/resolvers/zod";
import { MouseEventHandler, use } from "react";
import { Control, Resolver, useForm } from "react-hook-form";
import { CreateProjectRequest } from "tng-address-collection-shared/types/apiTypes.ts";
import { fallbackCreateProject } from "../defaults/fallbackProject.tsx";
import { NotUniqueError } from "../hooks/errors.ts";
import { useCreateProject } from "../hooks/useCreateProject.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { FormContainer } from "./FormContainer.tsx";
import { ProjectFormFields } from "./ProjectFormFields.tsx";

export const CreateProjectDialog = ({ onClose }: { onClose: () => void }) => {
  const translate = useTranslator();
  const { displayError, displaySuccess } = use(SnackbarContext);

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(
      CreateProjectRequest,
    ) as Resolver<CreateProjectRequest>,
    defaultValues: fallbackCreateProject,
  });

  const { mutate: createProject, isPending: isCreatePending } =
    useCreateProject();

  const onSubmit = (formData: CreateProjectRequest) => {
    createProject(formData, {
      onSuccess: () => {
        displaySuccess(translate("changesSaved"));
        onClose();
      },
      onError: (e) => {
        if (e instanceof NotUniqueError) {
          displayError(translate("errorProjectNameNotUnique"));
        } else {
          displayError(translate("errorSavingChanges"));
        }
      },
    });
  };

  return (
    <FormContainer
      title={translate("createProject")}
      onClose={onClose}
      saveConfig={{
        onSave: handleSubmit(onSubmit) as MouseEventHandler,
        isSaveDisabled: isCreatePending,
      }}
    >
      <ProjectFormFields control={control as Control<CreateProjectRequest>} />
    </FormContainer>
  );
};
